import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { StatusDotComponent } from 'app/core/components/status-dot/status-dot.component';
import { ColorPipe } from 'app/core/pipes/color.pipe';
import { SnagRiskPipe } from 'app/core/pipes/snag-risk.pipe';
import { AuthService } from '../../auth/auth.service';
import { FilterConfig, GlobalFilterPanelComponent } from '../components/global-filter-panel/global-filter-panel.component';
import { DashboardService } from '../dashboard.service';
import { DashboardEngineService } from '../services/dashboard-engine.service';
import { ActivityTimelineComponent } from '../widgets/shared/activity-timeline/activity-timeline.component';
import { WorkOrderExpiryTrackerComponent } from '../widgets/shared/work-order-expiry-tracker/work-order-expiry-tracker.component';
import { WorkQueueListComponent } from '../widgets/shared/work-queue-list/work-queue-list.component';

@Component({
  selector: 'app-facilities-dashboard',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    NgClass,
    DatePipe,
    RouterLink,
    StatusDotComponent,
    ColorPipe,
    SnagRiskPipe,
    GlobalFilterPanelComponent,
    WorkQueueListComponent,
    ActivityTimelineComponent,
    WorkOrderExpiryTrackerComponent,
  ],
  templateUrl: './facilities-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: block; width: 100%;' },
})
export class FacilitiesDashboardComponent {
  private engine = inject(DashboardEngineService);
  protected dashboardSvc = inject(DashboardService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private auth = inject(AuthService);

  readonly dashboardType = 'facilities';
  filters: any = {};

  serviceRequestsData: any = null;
  inspectionData: any = null;
  workOrderData: any = null;

  loadingServiceRequests = false;
  loadingInspection = false;
  loadingWo = false;

  readonly filterConfig: FilterConfig = {
    community: true,
    period: true,
    dateRange: false,
  };

  onFiltersChanged(f: any): void {
    this.filters = { ...f };
    this.dashboardSvc.activeFilters.set(this.filters);
    this.loadAll();
    this.cdr.markForCheck();
  }

  get hasWorkOrderPermission(): boolean {
    return this.auth.canAccess(['workorder_view']);
  }
  get hasInspectionPermission(): boolean {
    return this.auth.canAccess(['inspection_reports_view']);
  }

  private loadAll(): void {
    this.loadServiceRequests();
    this.loadInspection();
    this.loadWorkOrders();
  }

  private loadServiceRequests(): void {
    if (!this.hasWorkOrderPermission) return;
    this.loadingServiceRequests = true;
    this.engine
      .getWidgetData(this.dashboardType, 'serviceRequestsSummary', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.serviceRequestsData = res.record?.data ?? {};
          this.loadingServiceRequests = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingServiceRequests = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadInspection(): void {
    if (!this.hasInspectionPermission) return;
    this.loadingInspection = true;
    this.engine
      .getWidgetData(this.dashboardType, 'inspectionSummary', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.inspectionData = res.record?.data ?? {};
          this.loadingInspection = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingInspection = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadWorkOrders(): void {
    if (!this.hasWorkOrderPermission) return;
    this.loadingWo = true;
    this.engine
      .getWidgetData(this.dashboardType, 'workOrderSummary', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.workOrderData = res.record?.data ?? {};
          this.loadingWo = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingWo = false;
          this.cdr.markForCheck();
        },
      });
  }

  getPeriodLabel(): string {
    const map: Record<string, string> = {
      this_month: 'this month',
      last_month: 'last month',
      this_quarter: 'this quarter',
      last_quarter: 'last quarter',
      this_year: 'this year',
      all_time: 'all time',
    };
    return map[this.filters?.period] ?? 'this period';
  }

  getPriorityColor(p: string): string {
    const map: any = { critical: 'text-red-700 bg-red-50', high: 'text-orange-700 bg-orange-50', medium: 'text-yellow-700 bg-yellow-50', low: 'text-blue-700 bg-blue-50' };
    return map[p?.toLowerCase()] ?? 'text-gray-700 bg-gray-50';
  }

  getWorkOrderRoute(status: string): string {
    const map: Record<string, string> = {
      draft: 'inbox',
      rejected: 'inbox',
      'sent for review': 'sent-for-review',
      active: 'active',
      'completion request sent': 'active',
      completed: 'completed',
      terminated: 'terminated',
    };
    const tab = map[status?.toLowerCase()] ?? 'all';
    return '/procurement/work-orders/' + tab;
  }

  getWorkOrderQueryParams(status: string): any {
    const s = status?.toLowerCase();
    if (!s || s === 'sent for review' || s === 'completed' || s === 'terminated') return {};
    return { status: s };
  }

  serviceRequestQueryParams(type?: string): any {
    const params: any = { status: 'Pending' };
    if (this.filters?.association_id) params.association_id = this.filters.association_id;
    if (type) params.type = type;
    return params;
  }

  readonly openInspectionsQueryParams = { tab: 'all', status: ['In-Progress', 'Ready For Submission'] };
  readonly completedInspectionsQueryParams = { tab: 'approved' };

  severityQueryParams(risk: string): any {
    return { tab: 'all', risk };
  }

  categoryQueryParams(categoryId: number): any {
    return { tab: 'all', category: categoryId };
  }
}
